import React, { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom"; // Added useLocation
import Loader from "@/components/Loader";
import { useEventData } from "@/hooks/events/useEventData";
import Error from "@/components/Errors/Error";
import { useGalleryStore } from "@/store/gallery/galleryStore";
import { useEventStore } from "@/store/event/eventStore";
import DeletedEvent from "@/components/Events/Client/DeletedEvent";

interface EventProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that fetches event data for event-based routes
 * - Captures 'embed' status from URL and stores it in Zustand
 * - Calls useEventData to fetch event data and populate eventStore
 */
export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const params = useParams<{ eventId?: string }>();
  const location = useLocation(); // Initialize location hook
  
  // Get setter from Zustand store
  const setIsEmbed = useEventStore((state) => state.setIsEmbed);
  const setShowCover = useEventStore((state) => state.setShowCover);
  
  const eventKey = useGalleryStore.use.eventKey();
  const eventId = params.eventId;
  const existingEventData = useEventStore((state) => state.eventData);

  // 1. Logic to sync URL 'embed' param with Zustand store
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const isEmbedParam = queryParams.get("embed") === "true";
    const coverParam = queryParams.get("cover") !== "false";
    setShowCover(coverParam);
    
    // Update global store
    setIsEmbed(isEmbedParam);
    
  }, [location.search, setIsEmbed, setShowCover]);

  const { isLoading, error } = useEventData(eventId, eventKey);

  if (!eventId) {
    return <>{children}</>;
  }

  if (isLoading && !existingEventData) {
    return <Loader className="ht-100 w-100" />;
  }

  if (error) {
    const errorWithResponse = error as { response?: { status?: number }; status?: number; message?: string };
    const is404Error = (errorWithResponse?.message && String(errorWithResponse.message).includes("404"));

    if (is404Error) {
      return <DeletedEvent />;
    }

    return (
      <div className="container mt-5">
        <Error error={error} className="mt-5" />
      </div>
    );
  }

  return <>{children}</>;
};